import { useState, useCallback, createElement } from 'react'
import { pdf } from '@react-pdf/renderer'
import CEDECReport from '../components/CEDECReport'
import { generateRecommendations } from '../utils/recommendations'
import { downloadPDFViaBackend, downloadSimpleTextPDF } from '../utils/simplePdfDownload.js'
import { themesToDomains, calculateThemeScores } from '../utils/themeUtils'
import { generateAndSendPdf, generatePdfBlobAndBase64 } from '../utils/pdfBlobHelper.js'
import questionnaireConfig from '../config/questionnaire.json'

/**
 * Custom hook for managing assessment state and API calls
 */
/**
 * Determine urgency level based on scores
 */
const determineUrgencyLevel = (averageScore, criticalCount) => {
  if (criticalCount >= 3) return { level: 'CRITIQUE', color: '#dc3545', action: 'INTERVENTION IMMEDIATE' }
  if (criticalCount >= 1) return { level: 'URGENT', color: '#fd7e14', action: 'ACTION PRIORITAIRE' }
  if (averageScore <= 4) return { level: 'IMPORTANT', color: '#ffc107', action: 'PLAN D\'AMELIORATION' }
  if (averageScore >= 8) return { level: 'OPPORTUNITE', color: '#28a745', action: 'OPTIMISATION' }
  return { level: 'NORMAL', color: '#17a2b8', action: 'DEVELOPPEMENT' }
}

export const useAssessment = () => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [result, setResult] = useState(null)

  const submitAssessment = useCallback(async (clientInfo, assessmentData) => {
    setIsSubmitting(true)
    setError(null)
    setResult(null)

    try {
      const response = await fetch('/api/assessment/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          clientInfo,
          assessmentData
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error?.message || 'Erreur lors de la soumission')
      }

      const data = await response.json()
      setResult(data)
      return data

    } catch (err) {
      console.error('Erreur soumission assessment:', err)
      setError(err.message)
      throw err
    } finally {
      setIsSubmitting(false)
    }
  }, [])

  // The test and debug endpoints are admin only, so their calls carry the
  // admin session token (see doc/TEST_UTILITIES.md)
  const adminTestHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
  })

  const testPDF = useCallback(async () => {
    setIsSubmitting(true)
    setError(null)

    try {
      const response = await fetch('/api/assessment/test-pdf', {
        method: 'POST',
        headers: adminTestHeaders()
      })

      if (!response.ok) {
        throw new Error('Erreur lors de la génération du PDF de test')
      }

      // Download PDF
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'test-rapport-cedec.pdf'
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)

      return true

    } catch (err) {
      console.error('Erreur test PDF:', err)
      setError(err.message)
      throw err
    } finally {
      setIsSubmitting(false)
    }
  }, [])

  const testEmail = useCallback(async (email) => {
    setIsSubmitting(true)
    setError(null)

    try {
      const response = await fetch('/api/assessment/test-email', {
        method: 'POST',
        headers: adminTestHeaders(),
        body: JSON.stringify({ email })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error?.message || 'Erreur lors de l\'envoi du test')
      }

      const data = await response.json()
      return data

    } catch (err) {
      console.error('Erreur test email:', err)
      setError(err.message)
      throw err
    } finally {
      setIsSubmitting(false)
    }
  }, [])

  const checkHealth = useCallback(async () => {
    try {
      const response = await fetch('/api/health')
      
      if (!response.ok) {
        throw new Error('Service indisponible')
      }

      const data = await response.json()
      return data

    } catch (err) {
      console.error('Erreur health check:', err)
      throw err
    }
  }, [])

  const downloadPDF = useCallback(async (clientInfo, assessmentData) => {
    setIsSubmitting(true)
    setError(null)

    try {
      console.log('📄 Génération du PDF côté frontend...')
      console.log('🔍 Client Info:', clientInfo)
      console.log('🔍 Assessment Data:', assessmentData)
      
      // Generate recommendations using the recommendations engine
      console.log('🧠 Génération des recommandations...')
      
      // For frontend PDF generation, we need to adapt the data structure
      // Convert themes to domains format for compatibility with existing recommendation engine
      const domains = themesToDomains(assessmentData.themes, 15) // Longer names for PDF
      
      // Calculate theme averages for recommendations
      const themeScores = calculateThemeScores(assessmentData.themes, assessmentData.scores)
      
      const recommendations = generateRecommendations(
        themeScores,
        domains,
        clientInfo
      )
      console.log('✅ Recommandations générées:', recommendations)

      // Create PDF using React-PDF with improved helper
      console.log('📄 Création du PDF avec React-PDF et helper...')
      let pdfResult
      try {
        console.log('🔄 Utilisation du composant CEDEC unifié...')
        const pdfComponent = createElement(CEDECReport, {
          clientInfo,
          assessmentData,
          recommendations
        })
        
        pdfResult = await generatePdfBlobAndBase64(pdfComponent)
        console.log('✅ PDF créé avec succès:', pdfResult.sizeKB, 'KB')
        
      } catch (pdfError) {
        console.error('❌ Erreur création PDF React-PDF:', pdfError)
        throw new Error(`Erreur création PDF: ${pdfError.message}`)
      }

      // Download PDF with proper blob handling
      console.log('💾 Déclenchement du téléchargement...')
      
      // Use the blob from the result
      const pdfBlob = pdfResult.blob
      console.log('🔄 Utilisation du blob:', pdfBlob.size, 'bytes, type:', pdfBlob.type)
      
      // Create download URL
      const url = window.URL.createObjectURL(pdfBlob)
      console.log('🔗 URL créée:', url)
      
      // Create download link
      const a = document.createElement('a')
      a.href = url
      const now = new Date()
      const year = now.getFullYear()
      const month = String(now.getMonth() + 1).padStart(2, '0')
      a.download = `CEDEC-Resilience-${clientInfo.company.replace(/[^a-zA-Z0-9]/g, '-')}-${month}-${year}.pdf`
      a.style.display = 'none'
      
      // Add to DOM and trigger download
      document.body.appendChild(a)
      
      // Immediate download without setTimeout
      try {
        a.click()
        console.log('✅ Téléchargement déclenché')
        
        // Cleanup after a short delay
        setTimeout(() => {
          if (document.body.contains(a)) {
            document.body.removeChild(a)
          }
          window.URL.revokeObjectURL(url)
          console.log('✅ Nettoyage effectué')
        }, 1000)
        
      } catch (downloadError) {
        console.error('❌ Erreur lors du téléchargement:', downloadError)
        // Cleanup on error
        if (document.body.contains(a)) {
          document.body.removeChild(a)
        }
        window.URL.revokeObjectURL(url)
        throw downloadError
      }

      return true

    } catch (err) {
      console.error('❌ Erreur téléchargement PDF React-PDF:', err)
      
      // Try fallback: backend PDF generation
      try {
        console.log('🔄 Tentative de téléchargement via backend...')
        await downloadPDFViaBackend(clientInfo, assessmentData)
        console.log('✅ PDF téléchargé via backend avec succès')
        return true
      } catch (backendError) {
        console.error('❌ Erreur backend PDF:', backendError)
        
        // Last resort: simple text download
        try {
          console.log('📄 Téléchargement du rapport texte simple...')
          downloadSimpleTextPDF(clientInfo, assessmentData, recommendations)
          setError('PDF complexe indisponible. Rapport texte téléchargé.')
          return true
        } catch (textError) {
          console.error('❌ Toutes les méthodes de téléchargement ont échoué:', textError)
          setError(`Erreur PDF: ${err.message}. Contactez le support.`)
          throw err
        }
      }
    } finally {
      setIsSubmitting(false)
    }
  }, [])

  const sendAssessmentEmail = useCallback(async (clientInfo, assessmentData) => {
    setIsSubmitting(true)
    setError(null)

    try {
      console.log('📧 Envoi de l\'évaluation par email avec PDF généré...')
      console.log('🔍 Client Info:', clientInfo)
      console.log('🔍 Assessment Data:', assessmentData)
      
      // Generate PDF using the new helper utility
      console.log('📄 Génération du PDF côté frontend avec helper...')
      
      // Generate recommendations for PDF
      const domains = themesToDomains(assessmentData.themes, 15)
      const themeScores = calculateThemeScores(assessmentData.themes, assessmentData.scores)
      const recommendations = generateRecommendations(themeScores, domains, clientInfo)

      // ✅ Add validation fields required by backend (same as AdminTestPanel fix)
      const scores = Object.values(assessmentData.scores).filter(s => s !== null && s !== undefined)
      const averageScore = scores.length > 0 ? scores.reduce((a, b) => a + b, 0) / scores.length : 0
      
      // Ensure clientInfo has assessmentDate
      const clientInfoWithDate = {
        ...clientInfo,
        assessmentDate: clientInfo.assessmentDate || new Date().toISOString()
      }
      
      // Add backend validation fields to assessmentData
      const assessmentDataForBackend = {
        ...assessmentData,
        questionnaire: questionnaireConfig.questionnaire, // Backend validation requires full questionnaire
        averageScore: averageScore,
        completedAt: new Date().toISOString()
      }

      // Create PDF component
      const pdfComponent = createElement(CEDECReport, {
        clientInfo: clientInfoWithDate,
        assessmentData: assessmentDataForBackend,
        recommendations
      })

      // Use the new workflow helper with validated data
      const result = await generateAndSendPdf(
        pdfComponent,
        '/api/assessment/send-email-with-pdf',
        { clientInfo: clientInfoWithDate, assessmentData: assessmentDataForBackend }
      )

      if (!result.success) {
        throw new Error(result.error)
      }

      console.log('✅ Emails envoyés avec succès via nouveau système:', result.response)
      setResult(result.response)
      return result.response

    } catch (err) {
      console.error('❌ Erreur envoi email avec PDF:', err)
      
      // Fallback to legacy method
      try {
        console.log('🔄 Tentative avec méthode legacy...')
        const response = await fetch('/api/assessment/send-email', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            clientInfo,
            assessmentData
          })
        })

        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.error?.message || 'Erreur lors de l\'envoi des emails')
        }

        const data = await response.json()
        console.log('✅ Emails envoyés avec succès via méthode legacy:', data)
        setResult(data)
        return data
        
      } catch (fallbackError) {
        console.error('❌ Erreur méthode legacy:', fallbackError)
        setError(err.message)
        throw err
      }
    } finally {
      setIsSubmitting(false)
    }
  }, [])

  const testSMTPConfig = useCallback(async () => {
    setIsSubmitting(true)
    setError(null)

    try {
      console.log('🧪 Test de la configuration SMTP...')
      
      const response = await fetch('/api/assessment/test-smtp', {
        method: 'GET',
        headers: adminTestHeaders()
      })

      if (!response.ok) {
        throw new Error('Erreur lors du test SMTP')
      }

      const data = await response.json()
      console.log('✅ Test SMTP:', data)
      return data

    } catch (err) {
      console.error('❌ Erreur test SMTP:', err)
      setError(err.message)
      throw err
    } finally {
      setIsSubmitting(false)
    }
  }, [])

  const testEmailConfig = useCallback(async (email) => {
    setIsSubmitting(true)
    setError(null)

    try {
      console.log(`🧪 Test d'email vers ${email}...`)
      
      const response = await fetch('/api/assessment/test-email-config', {
        method: 'POST',
        headers: adminTestHeaders(),
        body: JSON.stringify({ email })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error?.message || 'Erreur lors du test email')
      }

      const data = await response.json()
      console.log('✅ Test email:', data)
      return data

    } catch (err) {
      console.error('❌ Erreur test email:', err)
      setError(err.message)
      throw err
    } finally {
      setIsSubmitting(false)
    }
  }, [])

  const testPdfBlobEmail = useCallback(async (clientInfo, assessmentData, testEmail) => {
    setIsSubmitting(true)
    setError(null)

    try {
      console.log('🧪 Test complet PDF blob -> email...')
      
      // Generate PDF blob locally first to test the conversion
      const domains = themesToDomains(assessmentData.themes, 15)
      const themeScores = calculateThemeScores(assessmentData.themes, assessmentData.scores)
      const recommendations = generateRecommendations(themeScores, domains, clientInfo)

      // Create PDF component
      const pdfComponent = createElement(CEDECReport, {
        clientInfo,
        assessmentData,
        recommendations
      })

      // Generate PDF and base64
      const pdfResult = await generatePdfBlobAndBase64(pdfComponent)
      console.log('✅ PDF test généré localement:', pdfResult.sizeKB, 'KB')

      // Test the new blob email endpoint
      const response = await fetch('/api/assessment/test/pdf-blob-email', {
        method: 'POST',
        headers: adminTestHeaders(),
        body: JSON.stringify({
          scenario: 'mixed',
          testEmail: testEmail || clientInfo.email
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error?.message || 'Erreur lors du test PDF blob email')
      }

      const data = await response.json()
      console.log('✅ Test PDF blob email terminé:', data)
      return data

    } catch (err) {
      console.error('❌ Erreur test PDF blob email:', err)
      setError(err.message)
      throw err
    } finally {
      setIsSubmitting(false)
    }
  }, [])

  const resetState = useCallback(() => {
    setIsSubmitting(false)
    setError(null)
    setResult(null)
  }, [])

  return {
    // State
    isSubmitting,
    error,
    result,
    
    // Actions
    submitAssessment,
    downloadPDF,
    sendAssessmentEmail,
    testPDF,
    testEmail,
    testSMTPConfig,
    testEmailConfig,
    testPdfBlobEmail,
    checkHealth,
    resetState
  }
}

export default useAssessment
